// THE-1084 PR4 — display rules for a `kind: 'LINK'` giftActivity (spec
// sections 4-6: grid tile, detail page, "everywhere else saves appear").
// Single source of truth so every surface (grid, detail page, shared list,
// accessible names) renders an added link identically instead of each
// call site re-deriving "named vs unnamed" on its own.

export interface AddedLink {
  url: string;
  name?: string;
  storeHost: string;
}

/** Store address exactly as the spec's grid/detail/shared-list rules show
 *  it: no `www.`. An IDN label (`xn--…`) is shown in its punycode form
 *  as-is rather than decoded to Unicode — no fixed block list of
 *  "confusable" scripts can stay complete (board QA, 2026-10-21, #836: a
 *  Latin-range homoglyph and an un-blocklisted Cherokee label both slipped
 *  past the previous Greek/Cyrillic/Armenian guard, and the client's own
 *  RFC 3492 decoder disagreed with the server's round-trip-validating one on
 *  which labels even decode). Punycode is always ASCII and always
 *  unambiguous, so leaving it undecoded is both simpler and safer than
 *  chasing an ever-growing confusable-script list. */
export function formatStoreHost(storeHost: string): string {
  return storeHost.toLowerCase().startsWith('www.') ? storeHost.slice(4) : storeHost;
}

/** Spec §4/§5 fallback title for an added link with no name: "A gift idea
 *  from {store address}". */
export function addedLinkFallbackTitle(storeHost: string): string {
  return `A gift idea from ${formatStoreHost(storeHost)}`;
}

/** The one display title for an added link — the typed name (plain text,
 *  shown exactly as typed), or the fallback. Compute this once at the doc→
 *  display-item boundary (same convention as a catalog product's frozen
 *  `productSnapshot.title`) so every consumer can just read `item.title`. */
export function addedLinkTitle(addedLink: AddedLink): string {
  return addedLink.name?.trim() || addedLinkFallbackTitle(addedLink.storeHost);
}

/** Strips the scheme (`https://`, `http://`) for display — it tells a reader
 *  nothing and only eats space the host/path need once the caller's CSS
 *  truncates the line (Kate, board QA on #836). */
function stripUrlScheme(url: string): string {
  return url.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
}

/** Spec §4: grid's second line. Named → the store address (no price, ever).
 *  Unnamed → the raw link itself (scheme stripped), truncated by the
 *  caller's CSS — "what tells two unnamed items apart" so a list of them
 *  isn't identical. */
export function addedLinkGridLine(addedLink: AddedLink): string {
  return addedLink.name?.trim() ? formatStoreHost(addedLink.storeHost) : stripUrlScheme(addedLink.url);
}

/** Spec §5: detail-page caption lines under the title.
 *  Named, own list    → "{store} · added by you"
 *  Named, shared list  → "{store}" only (friends never see "added by you")
 *  Unnamed, own list   → "Added by you" + a separate link line
 *  Unnamed, shared list → link line only, no "Added by you" */
export function addedLinkDetailMeta(
  addedLink: AddedLink,
  readOnly: boolean,
): { captionLine?: string; linkLine?: string } {
  const store = formatStoreHost(addedLink.storeHost);
  if (addedLink.name?.trim()) {
    return { captionLine: readOnly ? store : `${store} · added by you` };
  }
  return {
    captionLine: readOnly ? undefined : 'Added by you',
    linkLine: stripUrlScheme(addedLink.url),
  };
}

/** Spec §4: "an unnamed item's spoken name includes its link, so a list of
 *  them isn't a row of identical 'A gift idea from amazon.com'." Named
 *  items and ordinary Thea products just speak their title. */
export function savedItemAccessibleName(item: {
  title: string;
  kind?: 'LINK';
  addedLink?: AddedLink;
}): string {
  if (item.kind === 'LINK' && item.addedLink && !item.addedLink.name?.trim()) {
    return `${item.title}: ${item.addedLink.url}`;
  }
  return item.title;
}

/** THE-1142 — Kate's call (2026-10-22): GA4's terms forbid PII, and a pasted
 *  link's typed Name box is free text (and its URL can carry a registry/
 *  wish-list/shared-cart name or email) — so GA4 must never see either for a
 *  `kind: 'LINK'` item. Use this constant in place of `item.title` at every
 *  GA4 call site that fires for a product-or-link item. The real typed name
 *  still reaches Firestore unchanged via the first-party sink (`logEvent`),
 *  which this does not touch. */
export const GA_ADDED_LINK_ITEM_NAME = 'added_link';

/** GA4-safe stand-in for `item.title`: the constant above for a pasted link,
 *  the real title for a catalog product. */
export function gaSafeItemName(item: { kind?: 'LINK'; title: string }): string {
  return item.kind === 'LINK' ? GA_ADDED_LINK_ITEM_NAME : item.title;
}

/** GA4-safe stand-in for `item.productUrl` at a click-out: the bare store
 *  address (no scheme, no path — never the full pasted URL) for a link,
 *  the real destination for a catalog product. Meta can't use a pasted-link
 *  product view anyway (it isn't in the catalog), so callers skip the Meta
 *  pixel for a LINK item entirely rather than calling this for Meta. */
export function gaSafeDestination(item: {
  kind?: 'LINK';
  productUrl?: string;
  addedLink?: AddedLink;
}): string {
  if (item.kind === 'LINK') {
    return item.addedLink ? formatStoreHost(item.addedLink.storeHost) : '';
  }
  return item.productUrl ?? '';
}
